import { useEffect, useState } from "react";
import { logWarn } from "@/lib/log";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { api, money } from "@/lib/site";

const Shell = ({ children, testid }) => (
  <section className="min-h-[80vh] grid place-items-center px-5 pt-32 pb-20 grid-lines">
    <div className="max-w-lg w-full rounded-[2rem] bg-white border border-slate-200 p-10 text-center shadow-[0_24px_60px_rgba(15,23,42,0.08)]" data-testid={testid}>{children}</div>
  </section>
);

const statusUrl = (method, ref) => method === "paypal" ? `/payments/paypal/status/${ref}` : `/payments/stripe/status/${ref}`;

const resolveStatus = (data) => {
  if (data.payment_status === "paid") return { status: "paid", ...data };
  if (["expired", "failed"].includes(data.payment_status)) return { status: "failed" };
  return null;
};

function usePaymentStatus(method, ref) {
  const [state, setState] = useState({ status: ref ? "checking" : "unknown" });
  useEffect(() => {
    if (!ref) return undefined;
    let tries = 0, alive = true, timer;
    const poll = async () => {
      try {
        const result = resolveStatus((await api.get(statusUrl(method, ref))).data);
        if (result) return alive && setState(result);
      } catch (e) {
        if (e?.response?.status === 404) return alive && setState({ status: "pending" });
        logWarn("Payment status check failed, retrying", e?.message);
      }
      if (!alive) return;
      if (++tries < 10) timer = setTimeout(poll, 2000);
      else setState({ status: "pending" });
    };
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [method, ref]);
  return state;
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const method = params.get("method");
  const state = usePaymentStatus(method, method === "paypal" ? params.get("order_id") : params.get("session_id"));

  if (state.status === "checking") return <Shell testid="payment-checking"><Loader2 className="w-10 h-10 text-blue-600 mx-auto animate-spin" /><p className="mt-5 text-slate-600">Confirming your payment…</p></Shell>;
  if (state.status === "paid") return (
    <Shell testid="payment-success">
      <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
      <p className="overline mt-6">Payment confirmed</p>
      <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight">You're all set.</h1>
      <p className="mt-3 text-slate-600" data-testid="payment-success-details">{state.package_name} · {money(state.amount)} USD</p>
      <p className="mt-4 text-sm text-slate-500">We'll email you within one business day to schedule your discovery call.</p>
      <Link to="/" className="mt-8 inline-flex rounded-full bg-slate-900 hover:bg-blue-600 text-white px-6 py-3 font-semibold transition-colors" data-testid="payment-success-home-btn">Back to home</Link>
    </Shell>
  );
  return (
    <Shell testid="payment-unconfirmed">
      <XCircle className="w-12 h-12 text-amber-500 mx-auto" />
      <h1 className="mt-5 font-display text-2xl font-bold">We couldn't confirm the payment yet</h1>
      <p className="mt-3 text-slate-600">If you were charged, don't worry — we'll confirm by email. Otherwise you can try again from the pricing page.</p>
      <Link to="/pricing" className="mt-8 inline-flex rounded-full bg-blue-600 text-white px-6 py-3 font-semibold" data-testid="payment-unconfirmed-pricing-btn">Back to pricing</Link>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testid="payment-cancel">
      <p className="overline">Checkout cancelled</p>
      <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight">No problem — nothing was charged.</h1>
      <p className="mt-4 text-slate-600">Have a question before you decide? Start a chat or send us a message.</p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/pricing" className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-6 py-3 font-semibold hover:border-slate-900" data-testid="payment-cancel-pricing-btn"><ArrowLeft className="w-4 h-4" /> Back to pricing</Link>
        <Link to="/contact" className="inline-flex rounded-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 font-semibold" data-testid="payment-cancel-contact-btn">Contact us</Link>
      </div>
    </Shell>
  );
}
